import { api } from './client'

/** One setup parameter, described by the server. */
export interface RecipeParamField {
  key: string
  label: string
  /** `text`, `list` (comma/newline separated) or `select`. */
  kind: 'text' | 'list' | 'select'
  help: string | null
  placeholder: string | null
  default: unknown
  options: { value: string; label: string; disabled: boolean }[]
}

interface RecipeReadiness {
  state: 'ready' | 'needs_setup'
  reason: string | null
}

/** A resource a recipe can take its events from. */
interface RecipeSourceOption {
  id: string
  name: string
  kind: string
  readiness: RecipeReadiness
}

/** Shipped with chromaframe, or one of the workspace's own recipes. */
type RecipeOrigin = 'shipped' | 'workspace'

/** A recipe the workspace can set up, with the sources it can use. */
export interface RecipeView {
  id: string
  title: string
  description: string
  origin: RecipeOrigin
  /** The kind of resource it takes events from. */
  source: { kind: string; label: string }
  candidates: RecipeSourceOption[]
  default_topic_name: string
  suggested_template: string | null
}

/** A recipe set up in the workspace. */
export interface RecipeInstall {
  id: string
  recipe_id: string
  recipe_title: string
  origin: RecipeOrigin
  source: { id: string; name: string; kind: string }
  topic_id: string
  topic_name: string | null
  agent_id: string | null
  /** False while paused. */
  enabled: boolean
  /** Null when the recipe can't be read or no longer fits its source. */
  readiness: RecipeReadiness | null
  params: Record<string, unknown>
  param_fields: RecipeParamField[]
  /** What will install but not fully work. */
  warnings: string[]
}

/** What a person chooses when setting a recipe up, and can change later. */
export interface RecipeSetupBody {
  agent_id: string
  topic_id?: string
  topic_name?: string
  params: Record<string, unknown>
}

export interface CreateRecipeInstallBody extends RecipeSetupBody {
  recipe_id: string
  source_id: string
}

/** A recipe this build ships, with its manifest to start one from. */
export interface ShippedRecipe {
  id: string
  title: string
  description: string
  manifest: Record<string, unknown>
}

const installs = (workspaceId: string) =>
  `/api/workspaces/${workspaceId}/recipe-installs`

export const recipesApi = {
  /** The shipped recipes' manifests. */
  shipped: () => api.get<ShippedRecipe[]>('/api/recipes/shipped'),
  /** Recipes the workspace can set up. */
  list: (workspaceId: string) =>
    api.get<RecipeView[]>(`/api/workspaces/${workspaceId}/recipes`),
  /** A recipe's setup form for one source. */
  fields: (workspaceId: string, recipeId: string, sourceId: string) =>
    api.get<RecipeParamField[]>(
      `/api/workspaces/${workspaceId}/recipes/${encodeURIComponent(recipeId)}/fields?source=${encodeURIComponent(sourceId)}`,
    ),
  /** The workspace's installs; with `source`, the ones that use it. */
  installs: (workspaceId: string, opts: { source?: string } = {}) =>
    api.get<RecipeInstall[]>(
      opts.source
        ? `${installs(workspaceId)}?source=${encodeURIComponent(opts.source)}`
        : installs(workspaceId),
    ),
  install: (workspaceId: string, body: CreateRecipeInstallBody) =>
    api.post<RecipeInstall>(installs(workspaceId), body),
  /** Reconfigure: topic, agent, parameters. The source stays. */
  update: (workspaceId: string, installId: string, body: RecipeSetupBody) =>
    api.put<RecipeInstall>(`${installs(workspaceId)}/${installId}`, body),
  /** Pause or resume, keeping the setup. */
  setEnabled: (workspaceId: string, installId: string, enabled: boolean) =>
    api.put<void>(`${installs(workspaceId)}/${installId}/enabled`, {
      enabled,
    }),
  remove: (workspaceId: string, installId: string) =>
    api.delete<void>(`${installs(workspaceId)}/${installId}`),
}

/** Form text for a parameter's current (or default) value. */
export function paramText(field: RecipeParamField, value: unknown): string {
  const v = value ?? field.default
  if (Array.isArray(v)) return v.map(String).join(', ')
  return v == null ? '' : String(v)
}

/** A parameter's form text as the value the server expects. */
export function paramValue(field: RecipeParamField, text: string): unknown {
  if (field.kind === 'list')
    return text
      .split(/[\n,]+/)
      .map((s) => s.trim())
      .filter(Boolean)
  return text.trim()
}
